import { DockviewApi, DockviewPanelApi } from "dockview-react";
import { Data, PlotMouseEvent } from "plotly.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import { useCatalyzerStore } from "../stores/catalyzer";
import { api } from "./utils/api";
import { formatVolume } from "./utils/utils";

var initialTrace: any = {
  x: [],

  close: [],

  decreasing: { line: { color: "#7F7F7F" } },

  high: [],

  increasing: { line: { color: "#17BECF" } },

  line: { color: "rgba(31,119,180,1)" },

  low: [],

  open: [],

  type: "candlestick",
  xaxis: "x",
  yaxis: "y",
};

interface OptionDataRequest {
  ticker: string;
  startDate: string;
  endDate: string;
  interval: string;
}

interface BarChartData {
  x: string[];
  high: number[];
  low: number[];
  open: number[];
  close: number[];
  volume: number[];
}

function sliceBars(bars: BarChartData, n: number): BarChartData {
  return {
    x: bars.x.slice(0, n),
    open: bars.open.slice(0, n),
    high: bars.high.slice(0, n),
    low: bars.low.slice(0, n),
    close: bars.close.slice(0, n),
    volume: bars.volume.slice(0, n),
  };
}

export interface EquityChartInitParams {
  ticker: string;
  startDate: string;
  endDate: string;
  interval: string;
}

const CANDLE_COLORS = ["#17BECF", "#2ECC71", "#E67E22", "#9B59B6", "#E74C3C"];

const EquityChart = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
  initParams?: EquityChartInitParams;
}) => {
  const [dataRequest, setDataRequest] = useState<OptionDataRequest>({
    ticker: "",
    startDate: "",
    endDate: "",
    interval: "D",
  });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [risingColor, setRisingColor] = useState<string>(CANDLE_COLORS[0]);
  const [rawBars, setRawBars] = useState<BarChartData | null>(null);

  // Bar-by-bar reveal: when enabled, only the first `visibleCount` bars of
  // rawBars are plotted; +1/+10/+20 advance the reveal.
  const [barByBar, setBarByBar] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  // Track 2: second ticker overlaid on the first, both rebased to 100 —
  // mirrors the RealtimeChart component's comparison-mode logic.
  const [track2Enabled, setTrack2Enabled] = useState<boolean>(false);
  const [ticker2, setTicker2] = useState<string>("");
  const [rawBars2, setRawBars2] = useState<BarChartData | null>(null);
  const [risingColor2, setRisingColor2] = useState<string>(CANDLE_COLORS[1]);

  const [vlineTraces, setVlineTraces] = useState<any[]>([]);

  const inputEl = useRef<HTMLInputElement>(null);

  const catalyzerStore = useCatalyzerStore();
  const [catActive, setCatActive] = useState<boolean>(false);
  const hoveredItemIndex = useCatalyzerStore((s) => s.hoveredItemIndex);

  const toggleCat = () => {
    addCatalyzerData(!catActive);
    setCatActive(!catActive);

    console.log("Items in CAT: ", catalyzerStore.items);
  };

  function wrapByChars(input: string, limit = 45): string {
    const words = input.trim().split(/\s+/); // collapse whitespace
    const lines: string[] = [];
    let line = "";

    for (const w of words) {
      if (line.length === 0) {
        line = w; // start a new line
      } else if (line.length + 1 + w.length <= limit) {
        line += " " + w; // still fits
      } else {
        lines.push(line); // wrap
        line = w; // next line starts with this word
      }
    }
    if (line) lines.push(line);

    return lines.join("<br>");
  }

  const totalLen = rawBars?.x.length ?? 0;
  const sliceLen = barByBar ? Math.min(visibleCount, totalLen) : totalLen;

  // Base price traces (candlestick [+ close line], or the track-1/track-2
  // normalized-to-100 comparison pair when track2Enabled) — derived from the
  // raw fetched bars plus the current bar-by-bar window.
  const baseTraces = useMemo<any[]>(() => {
    if (!rawBars || sliceLen === 0) return [initialTrace];

    const primarySlice = sliceBars(rawBars, sliceLen);

    if (track2Enabled && rawBars2 && rawBars2.x.length > 0) {
      const sliceLen2 = barByBar ? Math.min(visibleCount, rawBars2.x.length) : rawBars2.x.length;
      if (sliceLen2 === 0) return [initialTrace];

      const secondarySlice = sliceBars(rawBars2, sliceLen2);
      const base1 = rawBars.open[0];
      const base2 = rawBars2.open[0];
      const name1 = dataRequest.ticker.toUpperCase() || "Track 1";
      const name2 = ticker2.toUpperCase() || "Track 2";

      return [
        {
          x: primarySlice.x,
          open: primarySlice.open.map((v) => (v / base1) * 100),
          high: primarySlice.high.map((v) => (v / base1) * 100),
          low: primarySlice.low.map((v) => (v / base1) * 100),
          close: primarySlice.close.map((v) => (v / base1) * 100),
          type: "candlestick",
          name: name1,
          increasing: { line: { color: risingColor } },
          decreasing: { line: { color: risingColor }, fillcolor: "rgba(0,0,0,0)" },
          hovertemplate: `O: %{open:.2f}  H: %{high:.2f}  L: %{low:.2f}  C: %{close:.2f}<br>Time: %{x}<br><extra>${name1}</extra>`,
          xaxis: "x",
          yaxis: "y",
          showlegend: true,
        },
        {
          x: secondarySlice.x,
          open: secondarySlice.open.map((v) => (v / base2) * 100),
          high: secondarySlice.high.map((v) => (v / base2) * 100),
          low: secondarySlice.low.map((v) => (v / base2) * 100),
          close: secondarySlice.close.map((v) => (v / base2) * 100),
          type: "candlestick",
          name: name2,
          increasing: { line: { color: risingColor2 } },
          decreasing: { line: { color: risingColor2 }, fillcolor: "rgba(0,0,0,0)" },
          hovertemplate: `O: %{open:.2f}  H: %{high:.2f}  L: %{low:.2f}  C: %{close:.2f}<br>Time: %{x}<br><extra>${name2}</extra>`,
          xaxis: "x",
          yaxis: "y2",
          showlegend: true,
        },
      ];
    }

    const lineTrace: Data = {
      x: primarySlice.x,
      y: primarySlice.close,
      type: "scatter",
      mode: "lines",
      opacity: 0.1,
      line: { color: "#1f77b4" },
      name: "Close",
      xaxis: "x",
      yaxis: "y",
      showlegend: true,
    };

    return [
      lineTrace,
      {
        ...initialTrace,
        increasing: { line: { color: risingColor } },
        x: primarySlice.x,
        high: primarySlice.high,
        low: primarySlice.low,
        open: primarySlice.open,
        close: primarySlice.close,
        name: "Price",
        text: primarySlice.volume.map((v: number) => formatVolume(v)),
        hovertemplate:
          "O: %{open}<br>H: %{high}<br>L: %{low}<br>C: %{close}<br>Vol: %{text}<extra></extra>",
      },
    ];
  }, [rawBars, rawBars2, sliceLen, visibleCount, barByBar, track2Enabled, risingColor, risingColor2, dataRequest.ticker, ticker2]);

  const volumeTrace = useMemo(() => {
    if (track2Enabled || !rawBars || sliceLen === 0) return null;
    const x = rawBars.x.slice(0, sliceLen);
    const volume = rawBars.volume.slice(0, sliceLen);
    return {
      x,
      y: volume,
      text: volume.map((v) => formatVolume(v)),
      type: "bar",
      marker: { color: "rgba(100, 149, 237, 0.35)" },
      xaxis: "x",
      yaxis: "y2",
      name: "Volume",
      hovertemplate: "Vol: %{text}<extra></extra>",
      showlegend: true,
    };
  }, [rawBars, sliceLen, track2Enabled]);

  const addCatalyzerData = (currentCatState: boolean) => {
    if (currentCatState === false) return; // don't add if turning off

    const items = catalyzerStore.items;

    // the trace carrying the primary series on the main "y" axis, whether
    // that's the plain candlestick or the track-1 side of a comparison plot
    const primaryCandle = baseTraces.find(
      (t: any) => t?.type === "candlestick" && t.yaxis !== "y2",
    );
    if (!primaryCandle?.low?.length || !primaryCandle?.high?.length) return;

    const yMin = Math.min(...primaryCandle.low);
    const yMax = Math.max(...primaryCandle.high);

    if (!items.length) {
      setVlineTraces([]);
      return;
    }

    let vlines = [];

    for (let i = 0; i < items.length; i++) {
      // content contains <mark> text </mark> tags, remove them and leave the text
      let clean = items[i]?.text?.replace(/<\/?mark>/g, "").trim();

      // remove anchor tags
      clean = clean.replace(/<a[^>]*>(.*?)<\/a>/g, "$1");

      // remove links
      clean = clean.replace(/https?:\/\/[^\s]+/g, "");

      // limit to 50 chars per line
      clean = wrapByChars(clean);

      const payload = [
        items[i].stringDate,
        clean ?? "no content data",
        items[i].url,
      ];
      const newyorkDate = items[i].nyTS;

      // Generate many points along the vline so hover detection works
      // at any height, not just near the two endpoints
      const numPoints = 20;
      const xArr = [];
      const yArr = [];
      const cdArr = [];
      for (let p = 0; p <= numPoints; p++) {
        xArr.push(newyorkDate);
        yArr.push(yMin + (yMax - yMin) * (p / numPoints));
        cdArr.push(payload);
      }

      vlines.push({
        x: xArr,
        y: yArr,
        type: "scatter",
        mode: "lines",
        customdata: cdArr,
        hovertemplate:
          "<b>%{customdata[0]}</b><br>" + // nyLabel
          "%{customdata[1]}<br>" +
          "<extra></extra>",
        hoverlabel: { bgcolor: "rgba(0,0,0,0.85)", font: { color: "white" } },

        showlegend: false,
        line: { color: "rgb(55, 128, 191)", width: 2 },
        name: "__vline__",
        xaxis: "x",
        yaxis: "y",
      });
    }

    setVlineTraces(vlines);
  };

  // when catalyzer updates, update the chart with vertical lines on the active timestmaps
  const items = useCatalyzerStore((s) => s.items);
  React.useEffect(() => {
    addCatalyzerData(catActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Recompute vlines whenever the displayed primary series changes shape
  // (new data, bar-by-bar window, track2 toggled) so their height keeps
  // matching the current y-range.
  useEffect(() => {
    addCatalyzerData(catActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTraces, catActive]);

  // Apply hover coloring: change hovered vline from blue to red
  const displayVlines = useMemo(() => {
    if (hoveredItemIndex === null) return vlineTraces;
    if (hoveredItemIndex < 0 || hoveredItemIndex >= vlineTraces.length) return vlineTraces;

    return vlineTraces.map((vline: any, i: number) =>
      i === hoveredItemIndex
        ? { ...vline, line: { ...vline.line, color: "rgb(255, 0, 0)" } }
        : vline,
    );
  }, [vlineTraces, hoveredItemIndex]);

  const finalData = useMemo(() => {
    const traces = volumeTrace ? [...baseTraces, volumeTrace] : [...baseTraces];
    return [...traces, ...displayVlines];
  }, [baseTraces, volumeTrace, displayVlines]);

  const normalizeRequest = (req: OptionDataRequest): OptionDataRequest => {
    const normalizeDate = (val: string, suffix: string) => {
      const v = val.trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v + suffix : v;
    };
    return {
      ...req,
      startDate: normalizeDate(req.startDate, " 00:00"),
      endDate: normalizeDate(req.endDate, " 23:59"),
    };
  };

  const requestData = async (req?: OptionDataRequest) => {
    const r = req ?? dataRequest;
    setErrorMessage("");
    setIsLoading(true);

    // save the form data in local storage
    localStorage.setItem("equityChartDataRequest", JSON.stringify(r));

    let responseData: BarChartData | null = null;

    try {
      const resp = await api.post<BarChartData>("/equity-chart", r);
      responseData = resp.data;
    } catch (err: any) {
      let errMsg = "";
      if (err?.response?.data?.detail) {
        errMsg = err.response.data.detail;
      } else if (err?.response?.statusText) {
        errMsg = err.response.statusText;
      } else if (err?.message) {
        errMsg = err.message;
      }
      setErrorMessage(errMsg || "Request failed");
      setIsLoading(false);
      return;
    }

    if (
      !responseData ||
      !responseData.x ||
      !responseData.high ||
      !responseData.low ||
      !responseData.open ||
      !responseData.close
    ) {
      setErrorMessage("Invalid data format received from server.");
      return;
    }

    setRawBars(responseData);
    setVisibleCount(barByBar ? 0 : responseData.x.length);

    setIsLoading(false);

    // set tab title to ticker
    if (props.api) {
      console.log("Setting title to ", dataRequest.ticker);
      const title = dataRequest.ticker.toUpperCase();
      props.api.setTitle(title);
    }
  };

  const requestData2 = async (req: OptionDataRequest) => {
    const secondTicker = ticker2.trim();
    if (!secondTicker) return;

    try {
      const resp = await api.post<BarChartData>("/equity-chart", { ...req, ticker: secondTicker });
      const responseData = resp.data;
      if (!responseData?.x || !responseData.open) {
        setErrorMessage("Invalid data format received from server for track 2.");
        return;
      }
      setRawBars2(responseData);
    } catch (err: any) {
      let errMsg = "";
      if (err?.response?.data?.detail) {
        errMsg = err.response.data.detail;
      } else if (err?.response?.statusText) {
        errMsg = err.response.statusText;
      } else if (err?.message) {
        errMsg = err.message;
      }
      setErrorMessage(errMsg || "Request failed for track 2");
    }
  };

  const onVlineClick = (url: string) => {
    console.log("opening: ", url);

    if (url !== "") {
      // open in new tab
      const w = window.open(url, "_blank", "noopener");
    }
  };

  const handleClick: (e: PlotMouseEvent) => void = (e) => {
    const pt = e.points?.[0];
    if (!pt || pt.data?.name !== "__vline__") return;

    // works because you set identical customdata for both line endpoints
    const cd =
      (pt.customdata as any) ?? (pt.data.customdata as any)?.[pt.pointIndex];
    const [nyTS, label, url] = Array.isArray(cd)
      ? cd
      : [cd?.ts, cd?.label, cd?.note];

    onVlineClick(url);
  };

  const autoSubmitRef = useRef(false);

  // focus on the input field when the component mounts
  useEffect(() => {
    if (props.initParams) {
      // Populate form from init params and flag for auto-submit
      setDataRequest({
        ticker: props.initParams.ticker,
        startDate: props.initParams.startDate,
        endDate: props.initParams.endDate,
        interval: props.initParams.interval,
      });
      autoSubmitRef.current = true;
    } else {
      // load the data request from local storage
      const storedRequest = localStorage.getItem("equityChartDataRequest");
      if (storedRequest) {
        setDataRequest(JSON.parse(storedRequest));
      }
    }

    if (inputEl.current) {
      inputEl.current.focus();
    }
  }, []);

  // Auto-submit after dataRequest is populated from initParams
  useEffect(() => {
    if (autoSubmitRef.current && dataRequest.ticker) {
      autoSubmitRef.current = false;
      // Activate CAT first, then request data — vlines are drawn once
      // baseTraces reflects the freshly loaded data (see effect above).
      setCatActive(true);
      requestData();
    }
  }, [dataRequest]);

  return (
    <div className="w-full h-full min-w-[300px] min-h-[300px] flex flex-col">
      <div className="flex gap-4 pl-2 pr-2 flex-wrap">
        <button
          className={` mt-2 btn ${catActive ? "btn-primary" : "btn-default"}`}
          onClick={(e) => {
            e.preventDefault();
            toggleCat();
          }}
        >
          CAT
        </button>

        <div className="flex gap-1 items-center mt-2">
          {CANDLE_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              title={color}
              onClick={() => setRisingColor(color)}
              style={{ backgroundColor: color }}
              className={`w-4 h-4 rounded-full border-2 ${risingColor === color ? "border-gray-800" : "border-transparent"}`}
            />
          ))}
        </div>

        <div className="flex gap-2 items-center mt-2">
          <label htmlFor="barByBar" className="text-sm cursor-pointer select-none">
            Bar by bar
          </label>
          <input
            type="checkbox"
            id="barByBar"
            checked={barByBar}
            onChange={(e) => {
              const checked = e.target.checked;
              setBarByBar(checked);
              if (checked) setVisibleCount(0);
            }}
          />
        </div>

        {barByBar && (
          <div className="flex gap-1 items-center mt-2">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setVisibleCount((v) => Math.min(v + 1, totalLen))}
            >
              +1
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setVisibleCount((v) => Math.min(v + 10, totalLen))}
            >
              +10
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setVisibleCount((v) => Math.min(v + 20, totalLen))}
            >
              +20
            </button>
          </div>
        )}

        <div className="flex gap-2 items-center mt-2">
          <label htmlFor="track2Toggle" className="text-sm cursor-pointer select-none">
            +Track
          </label>
          <input
            type="checkbox"
            id="track2Toggle"
            checked={track2Enabled}
            onChange={(e) => {
              const checked = e.target.checked;
              setTrack2Enabled(checked);
              if (!checked) {
                setRawBars2(null);
                setTicker2("");
              }
            }}
          />
        </div>

        {track2Enabled && (
          <div className="flex gap-2 items-center mt-2">
            <input
              placeholder="Ticker 2"
              type="text"
              value={ticker2}
              onChange={(e) => setTicker2(e.target.value)}
              className="w-24"
            />
            <div className="flex gap-1 items-center">
              {CANDLE_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  title={color}
                  onClick={() => setRisingColor2(color)}
                  style={{ backgroundColor: color }}
                  className={`w-4 h-4 rounded-full border-2 ${risingColor2 === color ? "border-gray-800" : "border-transparent"}`}
                />
              ))}
            </div>
          </div>
        )}

        <form
          className="flex flex-row m-2 gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            const normalized = normalizeRequest(dataRequest);
            setDataRequest(normalized);
            requestData(normalized);
            if (track2Enabled) requestData2(normalized);
          }}
        >
          <label htmlFor="ticker">
            Ticker:
            <input
              placeholder="Stock, ETF, Option"
              ref={inputEl}
              type="text"
              id="ticker"
              name="startDate"
              value={dataRequest.ticker}
              onChange={(e) => {
                setDataRequest({
                  ...dataRequest,
                  ticker: e.target.value,
                });
              }}
            />
          </label>

          <label htmlFor="startDate">
            Start Date:
            <input
              placeholder="YYYY-MM-DD HH:MM"
              type="text"
              id="startDate"
              name="startDate"
              value={dataRequest.startDate}
              onChange={(e) => {
                setDataRequest({ ...dataRequest, startDate: e.target.value });
              }}
            />
          </label>

          <label htmlFor="endDate">
            End Date:
            <input
              placeholder="YYYY-MM-DD HH:MM"
              type="text"
              id="endDate"
              name="endDate"
              value={dataRequest.endDate}
              onChange={(e) => {
                setDataRequest({ ...dataRequest, endDate: e.target.value });
              }}
            />
          </label>

          <label htmlFor="interval">
            Interval:
            <input
              placeholder="D, H, M, S"
              type="text"
              id="interval"
              name="endDate"
              value={dataRequest.interval}
              onChange={(e) => {
                setDataRequest({
                  ...dataRequest,
                  interval: e.target.value,
                });
              }}
            />
          </label>

          <button type="submit" className="hidden">
            Submit
          </button>
        </form>
      </div>
      <div className="flex-1 w-full h-full">
        <Chart
          chartData={finalData}
          isLoading={isLoading}
          errorMessage={errorMessage}
          handleClick={handleClick}
          catActive={catActive}
          track2Enabled={track2Enabled}
          hasRawData={!!rawBars}
          barByBar={barByBar}
        />
      </div>
    </div>
  );
};

export default EquityChart;

const Chart = (props: {
  chartData: any;
  isLoading: boolean;
  errorMessage: string;
  handleClick: (e: PlotMouseEvent) => void;
  catActive: boolean;
  track2Enabled: boolean;
  hasRawData: boolean;
  barByBar: boolean;
}) => {
  const idRef = useRef(Math.random().toString(36).slice(2));
  const setHoveredItemIndex = useCatalyzerStore((s) => s.setHoveredItemIndex);

  const baseCount = useMemo(
    () => props.chartData.filter((t: any) => t?.name !== "__vline__").length,
    [props.chartData],
  );

  const handleHover = React.useCallback(
    (e: any) => {
      if (!props.catActive) return;
      const pt = e.points?.[0];
      if (!pt || pt.data?.name !== "__vline__") {
        return;
      }
      const vlineIndex = pt.curveNumber - baseCount;
      if (vlineIndex >= 0) setHoveredItemIndex(vlineIndex);
    },
    [props.catActive, baseCount, setHoveredItemIndex],
  );

  const handleUnhover = React.useCallback(() => {
    if (props.catActive) setHoveredItemIndex(null);
  }, [props.catActive, setHoveredItemIndex]);

  if (props.errorMessage) {
    return <div className="text-red-500 ml-2">{props.errorMessage}</div>;
  }

  if (props.isLoading) {
    return <div className="ml-2">Loading...</div>;
  }

  if (props.chartData[0].x.length === 0) {
    if (props.hasRawData && props.barByBar) {
      return <div className="ml-2">Bar-by-bar ready</div>;
    }
    return <div className="ml-2">No data available</div>;
  }

  return (
    <Plot
    onClick={props.handleClick}
      onHover={handleHover}
      onUnhover={handleUnhover}
      data={props.chartData}
      layout={{
        uirevision: idRef.current,
        dragmode: "zoom",
        autosize: true,
        margin: { l: 40, r: 90, t: 25, b: 40 },
        showlegend: true,
        legend: { orientation: "h", y: 1.02, x: 0, xanchor: "left", yanchor: "bottom" },
        hovermode: "closest", // crosshairs
        hoverdistance: 25,
        xaxis: {
          autorange: true,
          domain: [0, 1],
          rangeslider: { visible: false },
          // range: ['2017-01-03 12:00', '2017-02-15 12:00'],
          type: "date",
          // crosshairs
          showspikes: true,
          spikesnap: "cursor",
          spikecolor: "#999",
          spikethickness: 1,
          spikemode: "toaxis+across+marker",
          spikedash: "solid",
          showgrid: true,
        },
        yaxis: {
          autorange: true,
          domain: props.track2Enabled ? [0, 1] : [0.25, 1],
          type: "linear",
          showspikes: true,
          spikemode: "toaxis+across+marker",
          spikesnap: "cursor",
          spikedash: "solid",
          spikecolor: "#999",
          spikethickness: 1,
          showgrid: true,
        },
        yaxis2: props.track2Enabled
          ? {
              domain: [0, 1],
              side: "right",
              overlaying: "y",
              type: "linear",
              autorange: true,
              showgrid: false,
              showspikes: true,
              spikemode: "toaxis+across+marker",
              spikesnap: "cursor",
              spikedash: "solid",
              spikecolor: "#999",
              spikethickness: 1,
            }
          : {
              domain: [0, 0.2],
              type: "linear",
              showticklabels: false,
              showgrid: false,
              fixedrange: true,
            },
      }}
      useResizeHandler={true}
      style={{
        width: "100%",
        height: "100%",
      }}
    />
  );
};
